"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSpotWorkerShiftForm = useSpotWorkerShiftForm;
const vue_1 = require("vue");
const luxon_1 = require("luxon");
const types_1 = require("../../types");
const common_1 = require("../../../js/common");
const common_2 = require("../../../common/common");
const timeZones_1 = __importDefault(require("../../../common/timeZones"));
const useSpotShiftStore_1 = require("../../stores/useSpotShiftStore");
function useSpotWorkerShiftForm() {
    let currentRole = undefined;
    let currentBranch = undefined;
    const store = (0, useSpotShiftStore_1.useSpotShiftStore)();
    const form = (0, vue_1.ref)({
        industry: 0,
        branchName: "",
        location: "",
        date: "",
        from: "",
        to: "",
        wage: 0,
        shiftCount: 0,
        contact: "",
        access: "",
        details: "",
        purpose: "",
    });
    const isEdit = (0, vue_1.ref)(false);
    const isShiftClaimed = (0, vue_1.ref)(false);
    const formValidation = (0, vue_1.ref)({});
    const isLoading = (0, vue_1.ref)(false);
    const selectedShift = (0, vue_1.ref)(undefined);
    const canVerify = (0, vue_1.ref)(false);
    const isShiftVerified = (0, vue_1.ref)(false);
    const totalPayment = (0, vue_1.computed)(() => {
        const start = (0, common_2.kitchenTimeToDuration)(form.value.from, "hours");
        const end = (0, common_2.kitchenTimeToDuration)(form.value.to, "hours");
        return (end - start) * form.value.wage;
    });
    const roleTimeLabel = (0, vue_1.computed)(() => {
        if (!isEdit.value || !selectedShift.value) {
            const from = toKitchenTime(form.value.from);
            const to = toKitchenTime(form.value.to);
            return `${from} ~ ${to}`;
        }
        return `${(0, common_2.dateToKitchenTime)(new Date(selectedShift.value.start))} ~ ${(0, common_2.dateToKitchenTime)(new Date(selectedShift.value.end))}`;
    });
    function toKitchenTime(value) {
        if (value.length === 3) {
            value = `0${value}`;
        }
        const { hour, min } = (0, common_2.extractTimeAndMin)(value, false);
        return hour !== undefined && min !== undefined ? `${hour}:${(0, common_1.addZeroPlaceholders)(min)}` : "";
    }
    function init(role, branch, shift) {
        currentRole = role;
        currentBranch = branch;
        if (currentBranch) {
            form.value.branchName = currentBranch.name;
            form.value.location = currentBranch.address || "";
        }
        isEdit.value = Boolean(shift);
        selectedShift.value = shift;
        isShiftClaimed.value = isEdit.value && shift?.userId !== 0;
        if (shift) {
            form.value.purpose = shift.purpose;
            form.value.wage = shift.wage;
            form.value.from = (0, common_2.dateToKitchenTime)(new Date(shift.start));
            form.value.to = (0, common_2.dateToKitchenTime)(new Date(shift.end));
            form.value.access = shift.access;
            form.value.industry = shift.industry;
            form.value.contact = shift.contact;
            form.value.date = (0, common_2.yearMonthDateString)(new Date(shift.start), timeZones_1.default.ASIA_TOKYO);
            form.value.details = shift.details;
            canVerify.value = new Date(shift.start).getTime() < new Date().getTime();
            isShiftVerified.value = shift.status === types_1.SpotShiftStatusType.COMPLETED || shift.status === types_1.SpotShiftStatusType.PAID || shift.status === types_1.SpotShiftStatusType.NO_SHOW;
            return;
        }
        if (currentRole) {
            form.value.purpose = currentRole.getName() || currentRole.purpose || "";
            form.value.wage = currentRole.getWage() || 0;
            form.value.from = `${(0, common_1.addZeroPlaceholders)(currentRole.getStartHour())}:${(0, common_1.addZeroPlaceholders)(currentRole.getStartMinute())}`;
            form.value.to = `${(0, common_1.addZeroPlaceholders)(currentRole.getEndHour())}:${(0, common_1.addZeroPlaceholders)(currentRole.getEndMinute())}`;
        }
    }
    function handleFromTimeChange(e) {
        const target = e.target;
        form.value.from = toKitchenTime(target.value);
    }
    function handleToTimeChange(e) {
        const target = e.target;
        form.value.to = toKitchenTime(target.value);
    }
    async function handleSubmit() {
        if (isLoading.value) {
            return {
                success: false,
                error: new Error("処理中です。しばらくお待ちください。"),
            };
        }
        if (!isFormValid()) {
            return {
                success: false,
                error: new Error("必須項目を入力してください。"),
            };
        }
        const payload = parseFormForSubmit();
        if (!payload) {
            return {
                success: false,
                error: new Error("店舗または職種が選択されていません。"),
            };
        }
        try {
            isLoading.value = true;
            if (selectedShift.value) {
                await store.updateShift(selectedShift.value.id, payload);
            }
            else {
                await store.createShift(payload);
            }
            return {
                success: true,
            };
        }
        catch (err) {
            console.error(err);
            return {
                success: false,
                error: err instanceof Error ? err : new Error(String(err)),
            };
        }
        finally {
            isLoading.value = false;
        }
    }
    async function handleDelete() {
        if (isLoading.value) {
            return {
                success: false,
                error: new Error("処理中です。しばらくお待ちください。"),
            };
        }
        if (!selectedShift.value) {
            return {
                success: false,
                error: new Error("対象のシフトが選択されていません。"),
            };
        }
        try {
            isLoading.value = true;
            await store.deleteShift(selectedShift.value.id);
            const diff = computeShiftDiff(selectedShift.value);
            const negativeDiff = {
                dateKey: diff.dateKey,
                laborHours: -diff.laborHours,
                laborCost: -diff.laborCost,
                totalCost: -diff.totalCost,
                numOfWorkers: -diff.numOfWorkers,
                roleCounts: Object.fromEntries(Object.entries(diff.roleCounts).map(([role, count]) => [role, -count])),
            };
            return {
                shiftDiff: negativeDiff,
                success: true,
            };
        }
        catch (err) {
            console.error(err);
            return {
                success: false,
                error: err instanceof Error ? err : new Error(String(err)),
            };
        }
        finally {
            isLoading.value = false;
        }
    }
    async function handleStatusChange(status) {
        if (!canVerify.value || isLoading.value) {
            return {
                success: false,
                error: new Error("ステータスを変更できません。"),
            };
        }
        try {
            isLoading.value = true;
            if (!selectedShift.value) {
                return {
                    success: false,
                    error: new Error("対象のシフトが選択されていません。"),
                };
            }
            await store.updateShiftStatus(selectedShift.value.id, status);
            isShiftVerified.value = true;
            return {
                success: true,
            };
        }
        catch (err) {
            console.error(err);
            return {
                success: false,
                error: err instanceof Error ? err : new Error(String(err)),
            };
        }
        finally {
            isLoading.value = false;
        }
    }
    function resetForm() {
        form.value = {
            industry: 0,
            branchName: "",
            location: "",
            date: "",
            from: "",
            to: "",
            wage: 0,
            shiftCount: 0,
            contact: "",
            access: "",
            details: "",
            purpose: "",
        };
        selectedShift.value = undefined;
        formValidation.value = {};
    }
    function isFormValid() {
        formValidation.value.industry = form.value.industry === 0;
        formValidation.value.branchName = !form.value.branchName.trim();
        formValidation.value.location = !form.value.location.trim();
        formValidation.value.date = !form.value.date.trim();
        formValidation.value.from = !form.value.from.trim();
        formValidation.value.to = !form.value.to.trim();
        formValidation.value.wage = form.value.wage <= 0;
        formValidation.value.shiftCount = !isEdit.value && (form.value.shiftCount < 1 || form.value.shiftCount > 20);
        formValidation.value.contact = !form.value.contact.trim();
        formValidation.value.access = !form.value.access.trim();
        formValidation.value.details = !form.value.details.trim();
        formValidation.value.purpose = !form.value.purpose.trim();
        return !Object.values(formValidation.value).some((f) => f);
    }
    const parseDates = () => {
        const dateStr = new Date(form.value.date);
        const year = dateStr.getFullYear();
        const month = dateStr.getMonth();
        const date = dateStr.getDate();
        const startTime = (0, common_2.extractHourAndMinFromKitchenTime)(toKitchenTime(form.value.from));
        const endTime = (0, common_2.extractHourAndMinFromKitchenTime)(toKitchenTime(form.value.to));
        return {
            startDate: new Date(year, month, date, startTime.hour, startTime.minute),
            endDate: new Date(year, month, date, endTime.hour, endTime.minute),
        };
    };
    function parseFormForSubmit() {
        const { startDate, endDate } = parseDates();
        if (!currentRole || !currentBranch) {
            return undefined;
        }
        const payload = {
            roleId: parseInt(currentRole.getId()),
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            industry: form.value.industry,
            branchId: currentBranch.id,
            wage: form.value.wage,
            shiftCount: form.value.shiftCount,
            purpose: currentRole.getName() || currentRole.purpose || form.value.purpose,
            contact: form.value.contact,
            access: form.value.access,
            details: form.value.details,
        };
        return payload;
    }
    function computeShiftDiff(shift) {
        const start = luxon_1.DateTime.fromISO(shift.start, { zone: timeZones_1.default.ASIA_TOKYO });
        const end = luxon_1.DateTime.fromISO(shift.end, { zone: timeZones_1.default.ASIA_TOKYO });
        const laborHours = end.diff(start, "hours").hours;
        const laborCost = laborHours * shift.wage;
        const dateKey = start.startOf("day").setZone("UTC").toISO();
        return {
            dateKey,
            laborHours,
            laborCost,
            totalCost: laborCost,
            numOfWorkers: 1,
            roleCounts: { [shift.roleId]: 1 },
        };
    }
    return {
        form,
        formValidation,
        isLoading,
        selectedShift,
        roleTimeLabel,
        isEdit,
        isShiftClaimed,
        canVerify,
        isShiftVerified,
        totalPayment,
        init,
        resetForm,
        isFormValid,
        handleFromTimeChange,
        handleToTimeChange,
        handleSubmit,
        handleDelete,
        handleStatusChange,
    };
}
